type SearchType = "key" | "name";

export interface TreeHelperOptions {
    readonly selectableTypes: string[];
    readonly onSelectFunction: (node: any) => void;
    readonly scrollIntoView: boolean;
    readonly isExactSearch: boolean;
}

class MerkmalSearchModel {
    searchTerm: string;
    searchType: SearchType;
}

class Hierarchy {
    key: string;
    pk: string;
    constructor(key: string, pk: string) {
        this.key = key;
        this.pk = pk;
    }
}

class TreeViewItem {
    key: string;
    pk?: string;
    text: string;
    items?: TreeViewItem[];
    isMatch?: boolean;
    expanded? = false;
    type: "Oberbegriff" | "Merkmal" | "Feld" | "Index" | "Hierarchie" | "Virtuell";
    iconClassName?: string;
    className?: string;
}

export class TreeHelper {

    private matches: string[];
    private expandedKeys = [];
    private hierarchy: { [index: string]: Hierarchy; } = {};
    private index: number;
    private items: TreeViewItem[] = [];
    private viewModel: kendo.data.ObservableObject;
    private isExactSearch;
    private scrollIntoView;
    
    private readonly selectableTypes: string[] = [];
    private readonly onSelectFunction: (node) => void;
    private readonly prevButtonElement: HTMLButtonElement;
    private readonly nextButtonElement: HTMLButtonElement;
    private readonly formElement: HTMLFormElement;
    private readonly searchTermElement: HTMLInputElement;
    private readonly treeElement: HTMLDivElement;

    constructor(options: TreeHelperOptions) {
        this.prev = this.prev.bind(this);
        this.next = this.next.bind(this);
        this.startSearch = this.startSearch.bind(this);
        this.treeElement = document.querySelector("div#modal_tree");
        this.prevButtonElement = this.treeElement.querySelector("button#btn_prev");
        this.prevButtonElement.addEventListener("click", this.prev);
        this.nextButtonElement = this.treeElement.querySelector("button#btn_next");
        this.nextButtonElement.addEventListener("click", this.next);
        this.formElement = this.treeElement.querySelector("form#form_filter");
        this.formElement.addEventListener("submit", this.startSearch);
        this.searchTermElement = this.treeElement.querySelector("input#searchTerm");
        this.selectableTypes = options.selectableTypes;
        this.onSelectFunction = options.onSelectFunction;
        this.scrollIntoView = options.scrollIntoView;
        this.isExactSearch = options.isExactSearch;
    }

    public initTree(items: TreeViewItem[], viewModel: kendo.data.ObservableObject): void {
        this.createHierarchy(items);
        this.items = items;
        this.viewModel = viewModel;
        this.updateTree();
    }

    public select(node: TreeViewItem) {
        if (typeof this.onSelectFunction === "function" && this.isSelectable(node)) {
            this.onSelectFunction(node);
        }
    }

    private isSelectable(node: TreeViewItem) {
        return this.selectableTypes.indexOf(node.type) > -1;
    }

    private createHierarchy(items: TreeViewItem[]) {
        if (items && items.length > 0) {
            items.forEach(item => {
                this.hierarchy[item.key] = new Hierarchy(item.key, item.pk);
                if (item.items && item.items.length > 0) {
                    this.createHierarchy(item.items);
                }
            });
        }
    }

    private enrichTree(items: TreeViewItem[]) {
        items.forEach(item => {
            item.expanded = this.expandedKeys && this.expandedKeys.length > 0 && this.expandedKeys.indexOf(item.key) !== -1;
            item.isMatch = this.matches && this.matches.length > 0 && this.matches.indexOf(item.key) !== -1;
            item.iconClassName = this.getIconClassName(item);
            item.className = this.getClassName(item);
            if (item.items && item.items.length > 0) {
                this.enrichTree(item.items);
            }
        });
    }

    private getIconClassName(item: TreeViewItem) {
        if (item !== null && item.type) {
            switch (item.type) {
                case "Oberbegriff":
                    return "fas fa-sitemap";
                case "Merkmal":
                    return "fas fa-square";
                case "Feld":
                    return "fas fa-list";
                case "Index":
                    return "fas fa-search";
                case "Hierarchie":
                    return "fas fa-cubes";
            }
        }
        return "fas fa-birthday-cake"; // unbekannter Knotentyp
    }

    private getClassName(item: TreeViewItem) {
        let className = "";
        if (item != null) {
            if (this.isSelectable(item)) {
                className += "selectable";
            }
            if (this.isCurrentMatch(item)) {
                className += " highlight";
            }
        }
        return className;
    }

    private isCurrentMatch(item: TreeViewItem) {
        if (this.matches && this.matches.length > 0 && this.index !== undefined) {
            return this.matches[this.index] === item.key;
        }
        return false;
    }

    private startSearch(e: Event) {
        this.search({
            searchTerm: this.searchTermElement.value,
            searchType: (this.treeElement.querySelector("input[name=searchType]:checked") as HTMLInputElement).value as SearchType,
        });
        e.preventDefault();
        return false;
    }

    private search(query: MerkmalSearchModel) {
        this.matches = [];
        this.index = 0;
        this.expandedKeys = [];
        this.filterBySearchType(query, this.items);
        if (this.matches && this.matches.length > 0) {
            this.expandByKey(this.matches[0]);
        }
        this.updateTree();
    }

    private updateTree() {
        this.enrichTree(this.items);
        this.viewModel.set("tree", this.items);
    }

    private filterBySearchType(query: MerkmalSearchModel, items: TreeViewItem[]) {
        switch (query.searchType) {
            case "name":
                this.filterByText({
                    items: items,
                    key: "ROOT",
                    text: "root",
                    type: "Oberbegriff",
                },
                    query.searchTerm);
                break;

            case "key":
                this.filterByKey({
                    items: items,
                    key: "ROOT",
                    text: "root",
                    type: "Oberbegriff"
                },
                    query.searchTerm);
                break;

            default:
                throw new Error("No such search type");
        }
    }


    private filterByKey(merkmal: TreeViewItem, searchTerm: string): void {
        const key = merkmal.key.toUpperCase();
        let isMatch: boolean;
        if (this.isExactSearch) {
            isMatch = key === searchTerm.toUpperCase();
        } else {
            isMatch = key.indexOf(searchTerm.toUpperCase()) > -1;
        }

        merkmal.isMatch = isMatch;
        if (isMatch) {
            this.matches.push(merkmal.key);
        }
        if (merkmal.items && merkmal.items.length > 0) {
            merkmal.items.forEach(item => {
                this.filterByKey(item, searchTerm);
            });
        }
    }

    private filterByText(merkmal: TreeViewItem, searchTerm: string): void {
        const name = merkmal.text.toUpperCase();
        let isMatch: boolean;
        if (this.isExactSearch) {
            isMatch = name === searchTerm.toUpperCase();
        } else {
            isMatch = name.indexOf(searchTerm.toUpperCase()) > -1;
        }
        merkmal.isMatch = isMatch;
        if (isMatch) {
            this.matches.push(merkmal.key);
        }
        if (merkmal.items && merkmal.items.length > 0) {
            merkmal.items.forEach(item => {
                this.filterByText(item, searchTerm);
            });
        }
    }

    private prev() {
        if (this.matches && this.matches.length > 0) {
            this.index--;
            if (this.index < 0) {
                this.index = this.matches.length - 1;
            }   
            this.expandedKeys = [];
            this.expandByKey(this.matches[this.index]);
            this.enrichTree(this.items);
            this.updateTree();
        }
    }

    private next() {
        if (this.matches && this.matches.length > 0) {
            this.index++;
            if (this.index >= this.matches.length) {
                this.index = 0;
            }
            this.expandedKeys = [];
            this.expandByKey(this.matches[this.index]);
            this.enrichTree(this.items);
            this.updateTree();
        }
    }

    private expandByKey(key: string) {
        if (!!key) {
            this.expandedKeys.push(key);
            if (this.hierarchy[key] && this.hierarchy[key].pk !== "root") {
                this.expandByKey(this.hierarchy[key].pk);
            } else {
                this.scrollTo();
            }
        }
    }

    private scrollTo(): void {
        if (this.scrollIntoView) {
            setTimeout(() => {
                let element = this.treeElement.querySelector(".highlight");
                if (element) {
                    element.scrollIntoView({ block: "center", behavior: "smooth", inline: "end" });
                }
            }, 400);
        }
    }
}
